import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Target, DollarSign, TrendingUp, Receipt } from 'lucide-react';

interface ConversionRateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversions: number;
  revenue: number;
  conversionRate: number;
  averageTicket: number;
  platformLogo?: string;
  platformName?: string;
}

export function ConversionRateModal({ 
  open, 
  onOpenChange, 
  conversions, 
  revenue, 
  conversionRate,
  averageTicket,
  platformLogo,
  platformName
}: ConversionRateModalProps) {
  useLanguage();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-[18px] font-semibold text-[#0F172A]">
            {translateInterfaceText("Detalhes de Taxa de Conversão")}</DialogTitle>
        </DialogHeader>
        
        {/* Main Metric Card */}
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-[24px_28px] mt-4">
          <div className="flex flex-col items-center justify-center text-center">
            <p className="text-[14px] font-medium text-[#64748B] mb-3">
              {translateInterfaceText("Taxa Média")}</p>
            <p className="text-[40px] font-bold text-[#0F172A] leading-none mb-3">
              {conversionRate.toFixed(2)}%
            </p>
            <p className="text-[13px] font-normal text-[#94A3B8]">
              {platformName ? translateInterfaceText("{value0} ficou no topo", { value0: (platformName) }) : translateInterfaceText("Ainda não há dados suficientes")}
            </p>
          </div>
        </div>
        
        {/* Secondary Metrics Grid */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-[#1D4FFF]" />
              <p className="text-[13px] font-medium text-[#6B7280]">{translateInterfaceText("Conversões")}</p>
            </div>
            <p className="text-[20px] font-semibold text-[#111827]">
              {conversions.toLocaleString(getInterfaceLanguage())}
            </p>
          </div>
          
          <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="h-4 w-4 text-[#1D4FFF]" />
              <p className="text-[13px] font-medium text-[#6B7280]">{translateInterfaceText("Ticket Médio")}</p>
            </div>
            <p className="text-[20px] font-semibold text-[#111827]">
              R$ {averageTicket.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2 })}
            </p>
          </div>
          
          <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-4 w-4 text-[#1D4FFF]" />
              <p className="text-[13px] font-medium text-[#6B7280]">{translateInterfaceText("Receita Total")}</p>
            </div>
            <p className="text-[20px] font-semibold text-[#111827]">
              R$ {revenue.toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
